import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Form } from "react-bootstrap";
import { Button } from "react-bootstrap";
import { listStaffRequirements, importRequirementsFromCrm } from "../../services/requirementsService";
import { errorToast, successToast } from "../Toasts/Toasts";
import CrmAccountLink from "./CrmAccountLink";
import { WAITING_ON, WaitingBadge } from "./WaitingOn";

// CMS: every requirement - from the portal, the WhatsApp feeder or the API -
// newest first, with where it stands for the customer and for pricing.

const CUSTOMER_STATUSES = ["Submitted", "Under Review", "Quoted", "Revision Requested", "Approved", "Rejected", "Ordered", "Closed"];
const inr = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
const when = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

const RequirementsQueue = () => {
  // A dashboard tile opens the queue already narrowed to what it counted:
  // pricing status and open-only come from the link, and show as a chip
  // that clears them.
  const [params, setParams] = useSearchParams();
  const pricing = params.get("status") || "";
  const openOnly = params.get("open") === "1";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [customerStatus, setCustomerStatus] = useState(params.get("customerStatus") || "");
  const [portalOnly, setPortalOnly] = useState(false);
  const [waitingOn, setWaitingOnFilter] = useState(params.get("waitingOn") || "");
  const [pulling, setPulling] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await listStaffRequirements({ q: q || undefined, customerStatus: customerStatus || undefined, waitingOn: waitingOn || undefined, status: pricing || undefined, open: openOnly ? 1 : undefined });
      setRows(portalOnly ? data.filter((r) => r.organizationId) : data);
    } catch (e) { errorToast("Could not load requirements"); }
  }, [q, customerStatus, waitingOn, portalOnly, pricing, openOnly]);
  useEffect(() => { load(); }, [load]);

  // Requirements written up from a visit live in the CRM and never came
  // through the app at all, so the queue could not see them. This pulls in
  // whatever is missing; anything already here is left alone.
  const pullFromCrm = async () => {
    setPulling(true);
    try {
      const r = await importRequirementsFromCrm();
      successToast(r.imported ? `Brought in ${r.imported} from the CRM` : "Nothing new in the CRM");
      if (r.skippedWithoutAccount?.length) {
        errorToast(`${r.skippedWithoutAccount.length} skipped with no Account: ${r.skippedWithoutAccount.join(", ")}`);
      }
      load();
    } catch (e) {
      errorToast(e?.message || "Could not reach the CRM");
    } finally {
      setPulling(false);
    }
  };

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h6 className="fw-bold mb-0">Requirements</h6>
          <div className="d-flex gap-2">
            {(pricing || openOnly) && (
              <span className="badge bg-light text-dark border align-self-center">
                {pricing ? `Pricing: ${pricing.split(",").join(", ")}` : ""}{pricing && openOnly ? " · " : ""}{openOnly ? "open only" : ""}
                <button type="button" className="btn btn-link btn-sm p-0 ms-1 text-decoration-none" title="Show all" onClick={() => setParams({})}>✕</button>
              </span>
            )}
            <Form.Control size="sm" placeholder="RQ / QT / account" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 200 }} />
            <Form.Select size="sm" value={customerStatus} onChange={(e) => setCustomerStatus(e.target.value)} style={{ width: 180 }}>
              <option value="">Any status</option>
              {CUSTOMER_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </Form.Select>
            <Form.Select size="sm" value={waitingOn} onChange={(e) => setWaitingOnFilter(e.target.value)} style={{ width: 170 }}>
              <option value="">Waiting on: all</option>
              <option value="any">Waiting on anyone</option>
              {WAITING_ON.map((w) => <option key={w}>{w}</option>)}
            </Form.Select>
            <Form.Check type="switch" id="portalOnly" label="Portal only" checked={portalOnly} onChange={(e) => setPortalOnly(e.target.checked)} className="small align-self-center" />
            <Button size="sm" variant="outline-success" disabled={pulling} onClick={pullFromCrm} title="Bring in requirements raised in Zoho - visits, mostly">
              {pulling ? "Pulling…" : "Pull from CRM"}
            </Button>
          </div>
        </div>
        <div className="card mt-2">
          <div className="card-body p-0">
            <table className="table table-hover table-sm mb-0">
              <thead><tr><th>RQ</th><th>Customer</th><th>Source</th><th>Received</th><th>Customer status</th><th>Pricing</th><th>Waiting on</th><th>Quote</th><th className="text-end">Total</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td><Link to={`/requirements/${r.number}`}>{r.number}</Link></td>
                    <td><CrmAccountLink zohoAccountId={r.zohoAccountId}>{r.organizationName || r.accountName}</CrmAccountLink>{r.organizationName ? <span className="badge bg-light text-dark ms-1">portal</span> : null}</td>
                    <td>{r.source}</td>
                    <td>{when(r.createdAt)}</td>
                    <td><span className="badge bg-light text-dark">{r.customerStatus}</span></td>
                    <td>{r.status}</td>
                    <td><WaitingBadge waitingOn={r.waitingOn} waitingSince={r.waitingSince} /></td>
                    <td>{r.quoteNumber || "—"}</td>
                    <td className="text-end">{inr(r.quoteTotal)}</td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={9} className="text-muted text-center py-4">No requirements match.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequirementsQueue;
